import React, { useEffect } from 'react';
import { observer } from 'mobx-react';

import { LayoutAside, LayoutBody, LayoutContent, LayoutContentTitle } from 'shared/layout';
import { ApplicationModule } from 'shared/modules';
import { UiDataBoundary } from 'shared/uikit';
import { useAsyncEffect, useDebounce, useNavigate, useObservable, useUrlParams, useUserWidgets, useValidation } from 'shared/hooks';
import { UiButton, UiDatepicker, UiFormControl, UiIcon, UiInput, UiModal, UiModalActions, UiModalContent, UiModalTitle, UiSelect, UiTextarea } from 'shared/uikit-new';
import { EntryPassModel } from 'shared/models';
import { entryPassesCreateQuery, entryPassesQuery } from 'shared/queries';
import { VmEntryPass } from 'shared/viewmodels';
import { COLORS, ROUTES } from 'shared/constants';
import { notifier, toDate } from 'shared/utilities';

import './index.scss';

import image from './assets/image.png';

export const EntryPassesPage = observer(function EntryPassesPage() {
    const navigate = useNavigate();
    const urlParams = useUrlParams({
        create: 0,
    });
    const store = useObservable({
        isLoading: true,
        isSuccess: true,
        entryPasses: [] as EntryPassModel[],
        errorMessage: '',
        isCreating: false,
        isCopying: false,
        isSubmitting: false,
        isSubmitted: false,
        entryPass: new EntryPassModel(),
        query: '',
        sort: null as string | null,
    });

    useEffect(() => {
        if(+urlParams.create === 1) {
            store.set('isCreating', true);
        }
    }, [store, urlParams.create]);

    useEffect(() => {
        ApplicationModule.setTitle('Пропуска');
    }, []);

    const load = async () => {
        store.set('isLoading', true);
        const { isSuccess, data, errorMessage } = await entryPassesQuery({ query: store.query, sort: store.sort });
        if (isSuccess && data) {
            store.set('entryPasses', data.items.map(item => new EntryPassModel(item)));
        } else {
            store.set('errorMessage', errorMessage);
        }

        store.set('isSuccess', isSuccess);
        store.set('isLoading', false);
    };

    useAsyncEffect(load, [store, store.sort]);

    const validation = useValidation(store.entryPass, (rules) => ({
        phone: rules.required(),
        visitor: rules.required(),
    }));

    const handleSubmit = async () => {
        store.set('isSubmitted', true);

        if (!validation.isValid || store.isSubmitting) {
            return;
        }
        store.set('isSubmitting', true);

        const { isSuccess, errorMessage } = await entryPassesCreateQuery(store.entryPass);
        if (isSuccess) {
            const { isSuccess, data, errorMessage } = await entryPassesQuery();
            if (isSuccess && data !== null) {
                store.set('entryPasses', data.items.map(item => new EntryPassModel(item)));
                store.set('isSubmitted', false);
            } else {
                notifier().alert({
                    title: 'Ошибка',
                    description: errorMessage,
                });
            }
        } else {
            notifier().alert({
                title: 'Ошибка',
                description: errorMessage,
            });
        }

        store.set('query', '');
        store.set('isCreating', false);
        store.set('isSubmitting', false);
    };

    const handleCreate = () => {
        store.set('isCopying', false);

        store.set('entryPass', new EntryPassModel({
            entryAt: toDate().format('YYYY-MM-DD'),
        }));

        store.set('isCreating', true);
    };

    const handleSearch = useDebounce(load, 1000);

    return (
        <LayoutBody>
            <LayoutContent>
                <LayoutContentTitle title={'Пропуска'}/>
                <div className="entry-passes-page__search">
                    <UiInput
                        isClearable
                        placeholder="Найти пропуск"
                        template="small"
                        icon={<UiIcon.Search size={20} />}
                        name="query"
                        value={store.query}
                        onChange={(data) => {
                            store.set('query', data.value || '');
                            handleSearch();
                        }}
                    />
                    <UiButton onClick={handleCreate}>
                        <UiIcon.IdCard size={20} />
                        <span>Оформить пропуск</span>
                    </UiButton>
                </div>
                <div className="entry-passes-page-sort">
                    <UiSelect
                        items={[{ id: null, name: 'Сначала новые' }, { id: 'asc', name: 'Сначала старые' }]}
                        name={'sort'}
                        value={store.sort}
                        onChange={store.handleChange}
                        slots={{
                            control: ({ onClick, item, isOpened }) => (
                                <div className="entry-passes-page-sort__control" onClick={onClick}>
                                    <span>{item?.name}</span>
                                    {isOpened && <UiIcon.ChevronUp size={16} />}
                                    {!isOpened && <UiIcon.ChevronDown size={16} />}
                                </div>
                            ),
                        }}
                    />
                </div>
                <UiDataBoundary
                    isLoading={store.isLoading}
                    isError={!store.isSuccess}
                    errorMessage={store.errorMessage}
                    actionText="Вернуться на главную"
                    onAction={() => navigate(ROUTES.HOME())}
                >
                    {store.entryPasses.length === 0 && (
                        <div className="entry-passes-page__empty">
                            <img src={image} alt="" />
                            <span>Список пропусков пуст</span>
                        </div>
                    )}
                    {store.entryPasses.length > 0 && (
                        <div>
                            {store.entryPasses.map(entryPass => (
                                <VmEntryPass
                                    key={entryPass.id}
                                    entryPass={entryPass}
                                    onCopy={(entryPass) => {
                                        store.set('entryPass', new EntryPassModel({
                                            ...entryPass,
                                            comment: entryPass.comment.replace('Оформлен на T-Net', ''),
                                        }));
                                        store.set('isCopying', true);
                                        store.set('isCreating', true);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </UiDataBoundary>
            </LayoutContent>
            <LayoutAside widgets={useUserWidgets()} />
            <UiModal
                isOpened={store.isCreating}
                onClose={() => {
                    store.set('isCreating', false);
                    navigate(null, {});
                }}
                isAside
            >
                <UiModalTitle style={{ color: COLORS._858991 }}>
                    {!store.isCopying && (
                        <>
                            <UiIcon.SquarePlus size={28} />
                            <span>Оформить пропуск</span>
                        </>
                    )}
                    {store.isCopying && (
                        <>
                            <UiIcon.PencilLine size={28} />
                            <span>Дублировать пропуск</span>
                        </>
                    )}
                </UiModalTitle>
                <UiModalContent>
                    <div className="grid grid-gap-24">
                        <UiFormControl label="Дата посещения" className="grid-col-6 grid-col-12--mobile">
                            <UiDatepicker withTime={false} name="entryAt" value={store.entryPass.entryAt} onChange={store.entryPass.handleChange} />
                        </UiFormControl>
                        <UiFormControl label={(<>ФИО посетителя <span>*</span></>)}
                            className="grid-col-6 grid-col-12--mobile"
                            error={store.isSubmitted && validation.visitor.errorMessage}
                        >
                            <UiInput placeholder="Введите имя посетителя" name="visitor" value={store.entryPass.visitor} onChange={store.entryPass.handleChange} />
                        </UiFormControl>
                        <UiFormControl label={'Гос. номер и марка автомобиля'} className="grid-col-6 grid-col-12--mobile">
                            <UiInput placeholder="А000АА 777; Рено Логан;" name="transport" value={store.entryPass.transport} onChange={store.entryPass.handleChange} />
                        </UiFormControl>
                        <UiFormControl label={(<>Телефон <span>*</span></>)} className="grid-col-6 grid-col-12--mobile" error={store.isSubmitted && validation.phone.errorMessage}>
                            <UiInput placeholder="+7(___) ___-__-__" name="phone" value={store.entryPass.phone} onChange={store.entryPass.handleChange} />
                        </UiFormControl>
                        <UiFormControl label={'Комментарий'} className="grid-col-12">
                            <UiTextarea placeholder="Оставьте ваш комментарий" name="comment" value={store.entryPass.comment} onChange={store.entryPass.handleChange} />
                        </UiFormControl>
                    </div>
                </UiModalContent>
                <UiModalActions>
                    <UiButton template="medium" onClick={handleSubmit} isLoading={store.isSubmitting}>
                        <span>Оформить пропуск</span>
                    </UiButton>
                    <UiButton
                        template="medium"
                        onClick={() => store.set('isCreating', false)}
                        style={{
                            backgroundColor: [COLORS._EAECF1, COLORS._C6CAD1],
                            color: [COLORS._2C2F35, COLORS._2C2F35],
                        }}
                    >
                        <span>Отмена</span>
                    </UiButton>
                </UiModalActions>
            </UiModal>
        </LayoutBody>
    );
});
